/**
 * Sunset painting ideas — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_painting_ideas`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const sunsetPaintingIdeasPack: ToilePack = {
    slug: 'sunset-painting-ideas',
    category: 'craft',
    name: 'Sunset painting ideas',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'palms', label: 'Palm silhouettes', subject: 'a canvas of black palm silhouettes against an orange and red sunset over the sea'},
        {key: 'canoe', label: 'Lake canoe', subject: 'a canvas of a sunset over a still lake with a canoe and pine silhouettes'},
        {key: 'grass', label: 'Through the grass', subject: 'a canvas of a low sun as a pale disc behind tall grass seed heads in gold'},
    ],
    styles: [
        {key: 'easy', label: 'Easy acrylic', style: 'easy beginner acrylic with a smooth warm gradient and flat black silhouettes', palette: 'warm oranges, pinks and gold blended into deep shadow', framing: 'The canvas on a wooden easel seen straight on, a plain pale wall behind'},
        {key: 'knife', label: 'Palette knife', style: 'thick palette-knife impasto with bold ridged strokes of color', palette: 'fiery saturated colors laid on in raised strokes', framing: 'The canvas leaning against a white wall on a light wooden floor'},
        {key: 'realism', label: 'Soft realism', style: 'soft realistic acrylic with fine glowing gradients and delicate detail', palette: 'natural warm light blended gently into the dark', framing: 'The canvas lying flat on a wooden work table seen from directly above, brushes and a paint palette beside it'},
    ],
    directions: ['mountain sunset', 'hot air balloon', 'purple sky'],
    directionHint: 'try: mountain sunset',
    seo: {
        title: 'Sunset Painting Ideas: Palms, Lake Canoe, Tall Grass',
        metaDescription: 'Nine sunset painting ideas for beginners: palms over an orange sea, a canoe on a lake and a sun through grass, in three styles.',
        h1: 'Sunset painting ideas: palm silhouettes, a canoe on a lake, a sun through tall grass',
        tagline: 'Nine sunset canvases, three silhouette scenes, each in easy acrylic, palette-knife impasto or soft realism.',
        intro: 'The sunset is the first canvas most beginners paint, a blend of warm color with a silhouette on top. These nine show three scenes: black palm silhouettes against an orange and red sunset over the sea, a sunset over a still lake with a canoe and pine silhouettes, and a low sun behind tall gold grass. Type another scene, such as a mountain sunset or a hot air balloon, and the same warm gradient carries it.',
        useCases: [
            'a first painting for a beginner',
            'a living room wall in warm tones',
            'a sunset for a dining room',
            'a gift for someone who loves the evening',
        ],
        faq: [
            {q: 'What makes a sunset easy to paint?', a: 'A warm gradient with a dark silhouette on top, which the three scenes show.'},
            {q: 'What is the sun picture?', a: 'A pale disc of sun seen through tall grass seed heads.'},
            {q: 'How do the three styles differ?', a: 'Easy acrylic is a smooth gradient, palette knife is raised ridged strokes, and soft realism is fine blended light.'},
            {q: 'Can I change the scene?', a: 'Yes. Type another scene and the warm gradient and the three styles carry it.'},
        ],
    },
}
